/**
 * Browser-level boolean preferences, backed by localStorage.
 *
 * Several settings are per browser rather than per account — the in-app
 * notification sound, dictation auto-send — and each needs the same three
 * things: a value read straight from storage, a setter that notifies every
 * mounted subscriber, and a `useSyncExternalStore` hook so a write in one
 * component (or one tab) re-renders everything showing it. That plumbing was
 * copied per preference and had already drifted — only the newest copy guarded
 * against storage throwing. This is the one copy.
 *
 * Encoding is fixed on purpose: the literals "on"/"off", where a **missing key
 * means the default**. That is what lets a new preference — or a changed
 * default — reach every existing browser with no backfill.
 *
 * Preferences that store something other than a boolean, or that encode it
 * differently (`notification-optin-dismissed` and `mobile-install-dismissed`
 * write "dismissed" and remove the key), keep their own modules; converting
 * them would mean rewriting values already on users' machines.
 */

import { useSyncExternalStore } from "react";

export interface BooleanPref {
  /** Current value, read straight from storage — safe to call outside a render. */
  readonly read: () => boolean;
  readonly set: (value: boolean) => void;
  /** Subscribes the calling component; re-renders on same- and cross-tab writes. */
  readonly useValue: () => boolean;
}

export function createBooleanPref(key: string, defaultValue: boolean): BooleanPref {
  const listeners = new Set<() => void>();

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    // Cross-tab changes arrive via the native storage event.
    const onStorage = (e: StorageEvent) => {
      if (e.key === key) listener();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      listeners.delete(listener);
      window.removeEventListener("storage", onStorage);
    };
  };

  // Only the literal that contradicts the default is honoured; a missing key —
  // or any value we never wrote — reads as the default.
  const contradiction = defaultValue ? "off" : "on";

  const read = (): boolean => {
    if (typeof window === "undefined") return defaultValue;
    try {
      return window.localStorage.getItem(key) === contradiction ? !defaultValue : defaultValue;
    } catch {
      return defaultValue; // storage blocked (private mode) — take the default
    }
  };

  const set = (value: boolean): void => {
    try {
      window.localStorage.setItem(key, value ? "on" : "off");
    } catch { /* storage unavailable — the preference just won't persist */ }
    listeners.forEach((listener) => { listener(); });
  };

  // The server snapshot is the default, so the hydration pass agrees with SSR.
  function useValue(): boolean {
    return useSyncExternalStore(subscribe, read, () => defaultValue);
  }

  return { read, set, useValue };
}
